import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchTradingInvTypes,
    fetchTradingCostCenters,
    fetchTradingItems,
    fetchTradingItemTempDetails,
    fetchTradingItemsGrid,
    addTempTradingItems,
    removeTradingStockItem,
    fetchTradingClients,
    fetchTradingSubClients,
    fetchTradingSubClientPO,
    fetchTradingClientGSTNos,
    fetchTradingCompanyGSTNos,
    fetchTradingTCSDCASDCAS,
    fetchTradingGSTConfig,
    submitTradingInvoice,
    clearTradingTempDetails,
    clearTradingItemsGrid,
    clearTradingSubClients,
    clearTradingGSTConfig,
    clearTradingCompanyGSTNos,
    resetTradingInvoice,
    selectTradingInvTypes,
    selectTradingCostCenters,
    selectTradingItems,
    selectTradingTempDetails,
    selectTradingItemsGrid,
    selectTradingClients,
    selectTradingSubClients,
    selectTradingPONumbers,
    selectTradingClientGSTNos,
    selectTradingCompanyGSTNos,
    selectTradingTCSDCASDCAS,
    selectTradingGSTConfig,
    selectTradingInvTypesLoading,
    selectTradingCCLoading,
    selectTradingItemsLoading,
    selectTradingTempLoading,
    selectTradingGridLoading,
    selectTradingClientsLoading,
    selectTradingSubCliLoading,
    selectTradingPOLoading,
    selectTradingClientGSTLoading,
    selectTradingCompanyGSTLoading,
    selectTradingGSTConfigLoading,
    selectTradingSaveLoading,
    selectTradingTempSaveLoading,
} from '../../slices/accountsSlice/clientTradingInvoiceSlice';

import {
    formatDateForAPI, tempKey, isValidQtyInput, isValidRateInput, tempQtyError,
    buildTempIssuePayload, computeItemRow, sumItems, gstTotal, invoiceTotal,
    validateInvoice, buildInvoicePayload, companyGSTNo,
} from './clientTradingInvoiceUtils';

import {
    ReceiptText, IndianRupee,
    ShieldCheck, Percent, CheckCircle,
    Loader2, ChevronDown, RotateCcw, Send,
    ShoppingCart, Plus, Trash2, Package, EyeOff, AlertCircle,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v) => {
    const n = parseFloat(v);
    if ((!v && v !== 0) || isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtQty = (v) => (parseFloat(v) || 0).toFixed(3);

const today = () => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; };

// ── Shared UI primitives (compact) ────────────────────────────────────────────
const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';
const readOnlyCls = `${inputCls} bg-gray-50 dark:bg-gray-900/40`;

const cellInput = (extraCls = '') =>
    `w-full px-2 py-0.5 text-right rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:border-indigo-400 ${extraCls}`;

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const Select = ({ value, onChange, disabled, loading, placeholder, options }) => (
    <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
            className={`${inputCls} appearance-none pr-8`}>
            <option value="">{loading ? 'Loading…' : placeholder}</option>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <SelectIcon loading={loading} />
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

const ReadOnly = ({ label, value }) => (
    <div>
        <Label>{label}</Label>
        <input type="text" value={value || ''} readOnly className={readOnlyCls} />
    </div>
);

// Small inline Yes/No segmented toggle
const YesNoToggle = ({ value, onChange, disabled }) => (
    <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
        {['Yes', 'No'].map(opt => (
            <button
                key={opt}
                type="button"
                disabled={disabled}
                onClick={() => onChange(opt)}
                className={`px-4 py-1 text-xs font-semibold transition-all disabled:opacity-60 ${
                    value === opt
                        ? opt === 'Yes'
                            ? 'bg-green-500 text-white'
                            : 'bg-rose-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >
                {opt}
            </button>
        ))}
    </div>
);

// ── Initial state ──────────────────────────────────────────────────────────────
const INITIAL_CLIENT = {
    Clientcode:        '',
    SubClientcode:     '',
    PONumber:          '',
    ClientInvoiceNo:   '',
    RANO:              '',
    InvoiceRemarks:    '',
    IsGstApplicable:   '',
    GSTType:           '',
    ClientGSTId:       '',
    CompanyGSTId:      '',
    IsTCSApplicable:   '',
    TCSAmount:         '',
};

const initialForm = () => ({
    CCType:            '',
    CCCode:            '',
    InvoiceDate:       today(),
    InvoiceMakingDate: today(),
    TCSDCA:            '',
    TCSSDCA:           '',
    ...INITIAL_CLIENT,
});

// ── Component ─────────────────────────────────────────────────────────────────
const ClientTradingInvoiceCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector(s => s.auth);
    const roleId = userData?.roleId  || userData?.RID  || 0;

    // Redux state
    const invTypes      = useSelector(selectTradingInvTypes);
    const costCenters   = useSelector(selectTradingCostCenters);
    const tradeItems    = useSelector(selectTradingItems);
    const tempDetails   = useSelector(selectTradingTempDetails);
    const itemsGrid     = useSelector(selectTradingItemsGrid);
    const clients       = useSelector(selectTradingClients);
    const subClients    = useSelector(selectTradingSubClients);
    const poNumbers     = useSelector(selectTradingPONumbers);
    const clientGSTNos  = useSelector(selectTradingClientGSTNos);
    const companyGSTNos = useSelector(selectTradingCompanyGSTNos);
    const tcsDCASDCAS   = useSelector(selectTradingTCSDCASDCAS);
    const taxConfig     = useSelector(selectTradingGSTConfig);

    const invTypesLoading   = useSelector(selectTradingInvTypesLoading);
    const ccLoading         = useSelector(selectTradingCCLoading);
    const itemsLoading      = useSelector(selectTradingItemsLoading);
    const tempLoading       = useSelector(selectTradingTempLoading);
    const gridLoading       = useSelector(selectTradingGridLoading);
    const clientsLoading    = useSelector(selectTradingClientsLoading);
    const subCliLoading     = useSelector(selectTradingSubCliLoading);
    const poLoading         = useSelector(selectTradingPOLoading);
    const clientGSTLoading  = useSelector(selectTradingClientGSTLoading);
    const companyGSTLoading = useSelector(selectTradingCompanyGSTLoading);
    const gstConfigLoading  = useSelector(selectTradingGSTConfigLoading);
    const saveLoading       = useSelector(selectTradingSaveLoading);
    const tempSaveLoading   = useSelector(selectTradingTempSaveLoading);

    // Local state
    const [form, setForm]         = useState(initialForm);
    const [selItem, setSelItem]   = useState('');
    const [tempQtys, setTempQtys] = useState({});   // stock picker: tempKey → qty
    const [rates, setRates]       = useState({});   // item grid: itemCode → selling rate
    const [checked, setChecked]   = useState({});   // item grid: itemCode → verified checkbox
    const [deleting, setDeleting] = useState('');
    const [errors, setErrors]     = useState([]);

    // Item grid rows: server data (qty, HSN, GST %) + the user's selling rate
    const items = itemsGrid.map(r => {
        const code = (r.OItemcode || '').trim();
        return {
            ...computeItemRow(r, rates[code] ?? ''),
            itemName:     r.OItemname || '',
            spec:         r.OItemspecification || '',
            units:        r.OUnits || '',
            purchasedAmt: parseFloat(r.OItemamt) || 0,
        };
    });
    const hasItems   = items.length > 0;
    const totals     = sumItems(items);
    const gstAdded   = form.IsGstApplicable === 'Yes' ? taxConfig : null;
    const gstAmt     = gstTotal(gstAdded, totals);
    const tcsAmt     = form.IsTCSApplicable === 'Yes' ? (parseFloat(form.TCSAmount) || 0) : 0;
    const grandTotal = invoiceTotal({ totals, taxConfig: gstAdded, isTCS: form.IsTCSApplicable, tcsAmount: form.TCSAmount });
    const tempTotal  = tempDetails.reduce((s, r) => s + (parseFloat(tempQtys[tempKey(r)]) || 0), 0);

    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchTradingInvTypes());
        dispatch(fetchTradingTCSDCASDCAS());
        return () => { dispatch(resetTradingInvoice()); };
    }, [dispatch]);

    useEffect(() => {
        setForm(p => ({ ...p, TCSDCA: tcsDCASDCAS?.TCSDCA || '', TCSSDCA: tcsDCASDCAS?.TCSSDCA || '' }));
    }, [tcsDCASDCAS]);

    // Stock picker: pre-fill each row's assigned qty like legacy (TempAssQty, normally 0.00)
    useEffect(() => {
        const init = {};
        tempDetails.forEach(r => { if (parseFloat(r.TempAssQty) > 0) init[tempKey(r)] = String(r.TempAssQty); });
        setTempQtys(init);
    }, [tempDetails]);

    // No items left → invoice details and taxes no longer apply (legacy hides them)
    useEffect(() => {
        if (!gridLoading && !hasItems) dispatch(clearTradingGSTConfig());
    }, [hasItems, gridLoading]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    // Legacy clearforGST: any change to client / GST choices drops the added taxes
    const clearTaxes = () => dispatch(clearTradingGSTConfig());

    const reloadItems = (ccCode) => {
        dispatch(fetchTradingItems(ccCode));
        dispatch(fetchTradingItemsGrid(ccCode));
    };

    const closePicker = () => {
        setSelItem('');
        setTempQtys({});
        dispatch(clearTradingTempDetails());
    };

    // ── Cascade Handlers ──────────────────────────────────────────────────────
    const handleCCTypeChange = (ccType) => {
        setForm(p => ({ ...initialForm(), CCType: ccType, TCSDCA: p.TCSDCA, TCSSDCA: p.TCSSDCA }));
        setRates({}); setChecked({}); setErrors([]);
        closePicker();
        clearTaxes();
        dispatch(clearTradingItemsGrid());
        dispatch(clearTradingSubClients());
        if (ccType) dispatch(fetchTradingCostCenters(ccType));
    };

    const handleCCChange = (ccCode) => {
        setForm(p => ({ ...p, ...INITIAL_CLIENT, CCCode: ccCode }));
        setRates({}); setChecked({}); setErrors([]);
        closePicker();
        clearTaxes();
        dispatch(clearTradingItemsGrid());
        dispatch(clearTradingSubClients());
        if (ccCode) {
            reloadItems(ccCode);
            dispatch(fetchTradingClients(ccCode));
        }
    };

    const handleClientChange = (clientcode) => {
        setForm(p => ({ ...p, Clientcode: clientcode, SubClientcode: '', PONumber: '', ClientGSTId: '' }));
        clearTaxes();
        dispatch(clearTradingSubClients());
        if (clientcode && form.CCCode) {
            dispatch(fetchTradingSubClients({ ccCode: form.CCCode, clientcode }));
        }
    };

    const handleSubClientChange = (subClientcode) => {
        setForm(p => ({ ...p, SubClientcode: subClientcode, PONumber: '', ClientGSTId: '' }));
        clearTaxes();
        if (subClientcode && form.CCCode && form.Clientcode) {
            dispatch(fetchTradingSubClientPO({ subClient: subClientcode, ccCode: form.CCCode, clientcode: form.Clientcode }));
            dispatch(fetchTradingClientGSTNos({ taxtype: 'SubClient', taxfor: subClientcode }));
        }
    };

    const handleInvoiceDateChange = (val) => {
        // Making date can't be earlier than the invoice date
        setForm(p => ({
            ...p,
            InvoiceDate: val,
            InvoiceMakingDate: val && p.InvoiceMakingDate && new Date(p.InvoiceMakingDate) < new Date(val) ? null : p.InvoiceMakingDate,
        }));
        clearTaxes();
    };

    // ── GST Handlers ──────────────────────────────────────────────────────────
    const handleGstApplicableChange = (v) => {
        setForm(p => ({ ...p, IsGstApplicable: v, ...(v === 'No' ? { GSTType: '', CompanyGSTId: '' } : {}) }));
        clearTaxes();
        if (v === 'No') dispatch(clearTradingCompanyGSTNos());
    };

    const handleGSTTypeChange = (gstType) => {
        setForm(p => ({ ...p, GSTType: gstType, CompanyGSTId: '' }));
        clearTaxes();
        dispatch(fetchTradingCompanyGSTNos(gstType))
            .unwrap()
            .then(list => { if (!list.length) toast.error('GSTs Not Exist'); })
            .catch(err => toast.error(err || 'Failed to load company GST numbers'));
    };

    const companyGSTText = companyGSTNos.find(g => String(g.TaxNoID) === form.CompanyGSTId)?.TaxNoName || '';
    const clientGSTText  = clientGSTNos.find(g => String(g.TaxNoID) === form.ClientGSTId)?.TaxNoName || '';

    const handleAddTaxes = () => {
        if (!form.PONumber)     { toast.warn('Select PO Number'); return; }
        if (!form.GSTType)      { toast.warn('Select GST Type'); return; }
        if (!form.ClientGSTId)  { toast.warn('Select Client GST'); return; }
        if (!form.CompanyGSTId) { toast.warn('Select GST'); return; }
        dispatch(fetchTradingGSTConfig({
            CompanyGST:        companyGSTNo(companyGSTText),
            VendorClientGSTNo: clientGSTText,
            InvoiceType:       'Client',
            Cccode:            form.CCCode,
            Taxtype:           form.GSTType,
            PONO:              form.PONumber,
            InvDate:           formatDateForAPI(form.InvoiceDate),
            Configtyp:         'Output GST',
            VendorOrClient:    'SubClient',
        }))
            .unwrap()
            .catch(err => toast.error(err || 'Output GST Data Is Not Configured'));
    };

    // ── Stock picker ──────────────────────────────────────────────────────────
    const handleItemSelect = (itemcode) => {
        setSelItem(itemcode);
        setTempQtys({});
        dispatch(clearTradingTempDetails());
        if (itemcode && form.CCCode) {
            dispatch(fetchTradingItemTempDetails({ itemcode, ccCode: form.CCCode }))
                .unwrap()
                .then(rows => { if (!rows.length) toast.warn('No stock available for this item'); })
                .catch(err => toast.error(err || 'Failed to load stock details'));
        }
    };

    const handleTempQtyChange = (row, value) => {
        if (!isValidQtyInput(value)) return;
        const error = tempQtyError(row, value);
        if (error) { toast.error(error); setTempQtys(p => ({ ...p, [tempKey(row)]: '' })); return; }
        setTempQtys(p => ({ ...p, [tempKey(row)]: value }));
    };

    const handleAddToInvoice = () => {
        const { error, payload } = buildTempIssuePayload({ rows: tempDetails, qtys: tempQtys, ccCode: form.CCCode, itemCode: selItem });
        if (error) { toast.warn(error); return; }

        dispatch(addTempTradingItems(payload))
            .unwrap()
            .then(code => {
                toast.success(`Item Code '${code}' Added Successfully`);
                closePicker();
                reloadItems(form.CCCode);
            })
            .catch(err => {
                toast.error(err || 'Error Occurred While Adding Items.');
                closePicker();
            });
    };

    // ── Item grid ─────────────────────────────────────────────────────────────
    const handleRemoveRow = (row) => {
        if (!checked[row.itemCode]) { toast.warn('Please Check for Delete'); return; }
        setDeleting(row.itemCode);
        dispatch(removeTradingStockItem({ DItemcode: row.itemCode, DCCCode: form.CCCode }))
            .unwrap()
            .then(() => {
                toast.success('Deleted Successfully');
                setRates(p => { const n = { ...p }; delete n[row.itemCode]; return n; });
                setChecked(p => { const n = { ...p }; delete n[row.itemCode]; return n; });
                reloadItems(form.CCCode);
            })
            .catch(err => toast.error(err || 'Error Occurred While Deleting Item'))
            .finally(() => setDeleting(''));
    };

    // ── Reset ─────────────────────────────────────────────────────────────────
    const handleReset = () => {
        setForm(initialForm());
        setRates({}); setChecked({}); setErrors([]); setSelItem(''); setTempQtys({});
        dispatch(resetTradingInvoice());
        dispatch(fetchTradingInvTypes());
        dispatch(fetchTradingTCSDCASDCAS());
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        const found = validateInvoice({ form, items, checked, taxConfig: gstAdded });
        setErrors(found);
        if (found.length) { toast.error('Please enter all required fields!'); return; }

        dispatch(submitTradingInvoice(buildInvoicePayload({
            form, items, taxConfig: gstAdded, companyGSTText, clientGSTText,
            userName: userData?.userName || '', roleId,
        })))
            .unwrap()
            .then(() => {
                toast.success('Client Trading Invoice Details Added Successfully.');
                handleReset();
            })
            .catch(err => toast.error(err || 'Error Occurred While Adding Client Trading Invoice Details.'));
    };

    // ── Derived option lists ──────────────────────────────────────────────────
    const typeOptions       = invTypes.map(t => ({ value: t.CCTypeId, label: t.CCTypename }));
    const ccOptions         = costCenters.map(c => ({ value: c.CC_Code, label: c.CC_Name }));
    const itemOptions       = tradeItems.map(i => ({ value: i.Itemcode, label: i.Itemname }));
    const clientOptions     = clients.map(c => ({ value: c.ClientCode, label: c.ClientName }));
    const subClientOptions  = subClients.map(c => ({ value: c.SubClientCode, label: c.SubClientCodename || c.SubClientCode }));
    const poOptions         = poNumbers.map(p => ({ value: p.PONumber, label: p.PONumber }));
    const clientGSTOptions  = clientGSTNos.map(g => ({ value: String(g.TaxNoID), label: g.TaxNoName }));
    const companyGSTOptions = companyGSTNos.map(g => ({ value: String(g.TaxNoID), label: g.TaxNoName }));

    const isBusy      = saveLoading;
    const clientLabel = clientOptions.find(c => c.value === form.Clientcode)?.label || form.Clientcode;
    const allChecked  = hasItems && items.every(r => checked[r.itemCode]);

    const thCls = 'px-2 py-2 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap';
    const tdCls = 'px-2 py-1.5';

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <ShoppingCart className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Client Trading Invoice Creation'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · Sell stock items to a client with per-item GST and HSN</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: form ───────────────────────────────────────────── */}
                <div className="lg:col-span-2 space-y-4 min-w-0">

                    {/* Setup + trading items */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={Package} title="Trading Items" />
                        <div className="p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
                                <div>
                                    <Label required>Invoice Type</Label>
                                    <Select value={form.CCType} onChange={handleCCTypeChange} disabled={isBusy}
                                        loading={invTypesLoading} placeholder="— Select Type —" options={typeOptions} />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label required>Cost Center</Label>
                                    <Select value={form.CCCode} onChange={handleCCChange} disabled={!form.CCType || ccLoading || isBusy}
                                        loading={ccLoading} placeholder={!form.CCType ? 'Select type first' : '— Select CC —'} options={ccOptions} />
                                </div>
                            </div>

                            {/* Item selector */}
                            {form.CCCode && (
                                <div className="p-3 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg border border-indigo-100 dark:border-indigo-800">
                                    <Label>Item Code</Label>
                                    <Select value={selItem} onChange={handleItemSelect} disabled={itemsLoading || tempSaveLoading || isBusy}
                                        loading={itemsLoading} placeholder="— Select Item —" options={itemOptions} />
                                </div>
                            )}

                            {/* Stock picker */}
                            {tempLoading && (
                                <div className="flex items-center justify-center py-3">
                                    <Loader2 className="h-4 w-4 text-indigo-500 animate-spin mr-2" />
                                    <span className="text-xs text-gray-500">Loading stock details…</span>
                                </div>
                            )}

                            {!tempLoading && tempDetails.length > 0 && (
                                <div className="rounded-lg border border-indigo-200 dark:border-indigo-800">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-indigo-50 dark:bg-indigo-900/30">
                                                    <th className={`${thCls} text-left`}>Received Date</th>
                                                    <th className={`${thCls} text-left`}>Item Code</th>
                                                    <th className={`${thCls} text-left`}>MRR / Ref No</th>
                                                    <th className={`${thCls} text-left`}>PO / Indent No</th>
                                                    <th className={`${thCls} text-right`}>Quantity</th>
                                                    <th className={`${thCls} text-right`}>Assigned Qty</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-indigo-100 dark:divide-indigo-800">
                                                {tempDetails.map(row => (
                                                    <tr key={tempKey(row)} className="hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors">
                                                        <td className={`${tdCls} text-gray-600 dark:text-gray-300 whitespace-nowrap`}>{row.Tempdate || ''}</td>
                                                        <td className={`${tdCls} font-medium text-gray-800 dark:text-gray-100`}>{row.TempItemcode || ''}</td>
                                                        <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{row.Tempmrr || ''}</td>
                                                        <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{row.Temppono || ''}</td>
                                                        <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300`}>{fmtQty(row.TempQuantity)}</td>
                                                        <td className={`${tdCls} text-right`}>
                                                            <input type="text" inputMode="decimal" placeholder="0.000"
                                                                value={tempQtys[tempKey(row)] ?? ''}
                                                                onChange={e => handleTempQtyChange(row, e.target.value)}
                                                                className={cellInput('w-24 border-indigo-200 dark:border-indigo-700')} />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="bg-indigo-50/60 dark:bg-indigo-900/20">
                                                    <td colSpan={5} className={`${tdCls} text-right text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase`}>Total Assigned</td>
                                                    <td className={`${tdCls} text-right font-bold text-indigo-600 dark:text-indigo-400`}>{fmtQty(tempTotal)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                    <div className="flex justify-end gap-2 p-2 border-t border-indigo-100 dark:border-indigo-800">
                                        <button type="button" onClick={closePicker} disabled={tempSaveLoading}
                                            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center gap-1 disabled:opacity-50">
                                            <EyeOff className="h-4 w-4" /> Hide
                                        </button>
                                        <button type="button" onClick={handleAddToInvoice} disabled={tempSaveLoading || isBusy}
                                            className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50 transition-colors">
                                            {tempSaveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                            Add
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Item code details */}
                            {gridLoading && (
                                <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
                                    <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> Loading items…
                                </div>
                            )}

                            {hasItems && (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-sm min-w-[1100px]">
                                        <thead>
                                            <tr className="bg-gray-50 dark:bg-gray-900/40">
                                                <th className={`${thCls} text-center`}>
                                                    <input type="checkbox" checked={allChecked} title="Verify all"
                                                        onChange={e => setChecked(Object.fromEntries(items.map(r => [r.itemCode, e.target.checked])))}
                                                        className="h-4 w-4 accent-indigo-600" />
                                                </th>
                                                <th className={`${thCls} text-left`}>Item</th>
                                                <th className={`${thCls} text-left`}>Units</th>
                                                <th className={`${thCls} text-right`}>Qty</th>
                                                <th className={`${thCls} text-right`}>Purchased Amt</th>
                                                <th className={`${thCls} text-right`}>Selling Rate</th>
                                                <th className={`${thCls} text-right`}>Selling Amt</th>
                                                <th className={`${thCls} text-right`}>CGST %</th>
                                                <th className={`${thCls} text-right`}>CGST Amt</th>
                                                <th className={`${thCls} text-right`}>SGST %</th>
                                                <th className={`${thCls} text-right`}>SGST Amt</th>
                                                <th className={`${thCls} text-right`}>IGST %</th>
                                                <th className={`${thCls} text-right`}>IGST Amt</th>
                                                <th className={`${thCls} text-left`}>HSN</th>
                                                <th className={`${thCls} text-center`}></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {items.map(row => (
                                                <tr key={row.itemCode} className="hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-colors">
                                                    <td className={`${tdCls} text-center`}>
                                                        <input type="checkbox" checked={!!checked[row.itemCode]}
                                                            onChange={e => setChecked(p => ({ ...p, [row.itemCode]: e.target.checked }))}
                                                            className="h-4 w-4 accent-indigo-600" />
                                                    </td>
                                                    <td className={`${tdCls} max-w-[220px]`}>
                                                        <p className="font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.itemCode}</p>
                                                        <p className="text-xs text-gray-600 dark:text-gray-300 truncate" title={row.itemName}>{row.itemName}</p>
                                                        {row.spec && <p className="text-[11px] text-gray-400 truncate" title={row.spec}>{row.spec}</p>}
                                                    </td>
                                                    <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{row.units}</td>
                                                    <td className={`${tdCls} text-right font-medium text-gray-700 dark:text-gray-200`}>{row.qty}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300 whitespace-nowrap`}>{fmt(row.purchasedAmt)}</td>
                                                    <td className={`${tdCls} text-right`}>
                                                        <input type="text" inputMode="decimal" placeholder="0.00" value={row.rate}
                                                            onChange={e => { const v = e.target.value; if (isValidRateInput(v)) setRates(p => ({ ...p, [row.itemCode]: v })); }}
                                                            className={cellInput('w-24')} />
                                                    </td>
                                                    <td className={`${tdCls} text-right font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap`}>{fmt(row.amount)}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300`}>{row.cgstPer}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300 whitespace-nowrap`}>{fmt(row.cgstAmt)}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300`}>{row.sgstPer}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300 whitespace-nowrap`}>{fmt(row.sgstAmt)}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300`}>{row.igstPer}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300 whitespace-nowrap`}>{fmt(row.igstAmt)}</td>
                                                    <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{row.hsn}</td>
                                                    <td className={`${tdCls} text-center`}>
                                                        <button type="button" onClick={() => handleRemoveRow(row)} disabled={!!deleting || isBusy}
                                                            title={checked[row.itemCode] ? 'Delete item' : 'Tick the row to delete'}
                                                            className="p-1 rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-50">
                                                            {deleting === row.itemCode ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-indigo-50/60 dark:bg-indigo-900/20 font-bold">
                                                <td colSpan={4} className={`${tdCls} text-right text-[11px] text-gray-600 dark:text-gray-400 uppercase`}>Total</td>
                                                <td className={`${tdCls} text-right text-gray-700 dark:text-gray-200 whitespace-nowrap`}>{fmt(itemsGrid[0]?.OSumTotal)}</td>
                                                <td />
                                                <td className={`${tdCls} text-right text-indigo-600 dark:text-indigo-400 whitespace-nowrap`}>{fmt(totals.basic)}</td>
                                                <td />
                                                <td className={`${tdCls} text-right text-gray-700 dark:text-gray-200 whitespace-nowrap`}>{fmt(totals.cgst)}</td>
                                                <td />
                                                <td className={`${tdCls} text-right text-gray-700 dark:text-gray-200 whitespace-nowrap`}>{fmt(totals.sgst)}</td>
                                                <td />
                                                <td className={`${tdCls} text-right text-gray-700 dark:text-gray-200 whitespace-nowrap`}>{fmt(totals.igst)}</td>
                                                <td colSpan={2} />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Invoice details — shown once items are added, like legacy */}
                    {hasItems && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <CardHeader icon={ReceiptText} title="Invoice Details" />
                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                                <div>
                                    <Label required>Client</Label>
                                    <Select value={form.Clientcode} onChange={handleClientChange} disabled={clientsLoading || isBusy}
                                        loading={clientsLoading} placeholder="— Select Client —" options={clientOptions} />
                                </div>
                                <div>
                                    <Label required>Sub Client</Label>
                                    <Select value={form.SubClientcode} onChange={handleSubClientChange} disabled={!form.Clientcode || subCliLoading || isBusy}
                                        loading={subCliLoading} placeholder={!form.Clientcode ? 'Select client first' : '— Select Sub Client —'} options={subClientOptions} />
                                </div>
                                <div>
                                    <Label required>PO Number</Label>
                                    <Select value={form.PONumber} onChange={v => { setForm(p => ({ ...p, PONumber: v })); clearTaxes(); }}
                                        disabled={!form.SubClientcode || poLoading || isBusy}
                                        loading={poLoading} placeholder={!form.SubClientcode ? 'Select sub-client first' : '— Select PO —'} options={poOptions} />
                                </div>
                                <div>
                                    <Label required>Client Invoice No.</Label>
                                    <input type="text" placeholder="Enter invoice number…" value={form.ClientInvoiceNo}
                                        onChange={e => setForm(p => ({ ...p, ClientInvoiceNo: e.target.value }))}
                                        disabled={isBusy} className={inputCls} />
                                </div>
                                <div>
                                    <Label required>RA No</Label>
                                    <input type="text" placeholder="Enter RA No…" value={form.RANO}
                                        onChange={e => setForm(p => ({ ...p, RANO: e.target.value }))}
                                        disabled={isBusy} className={inputCls} />
                                </div>
                                <div>
                                    <Label required>Invoice Date</Label>
                                    <CustomDatePicker value={form.InvoiceDate} onChange={handleInvoiceDateChange}
                                        maxDate={today()} placeholder="Select invoice date" disabled={isBusy} />
                                </div>
                                <div>
                                    <Label required>Invoice Making Date</Label>
                                    <CustomDatePicker value={form.InvoiceMakingDate} onChange={val => setForm(p => ({ ...p, InvoiceMakingDate: val }))}
                                        minDate={form.InvoiceDate || null} maxDate={today()} placeholder="Select making date" disabled={isBusy} />
                                </div>
                                <ReadOnly label="Basic Value" value={totals.basic.toFixed(2)} />
                                <div className="sm:col-span-2 lg:col-span-3">
                                    <Label required>Remarks</Label>
                                    <textarea rows={2} placeholder="Enter remarks…"
                                        value={form.InvoiceRemarks}
                                        onChange={e => setForm(p => ({ ...p, InvoiceRemarks: e.target.value }))}
                                        disabled={isBusy} className={`${inputCls} resize-y`} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tax — GST and TCS */}
                    {hasItems && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <CardHeader icon={ShieldCheck} title="Tax Configuration" />
                            <div className="divide-y divide-gray-100 dark:divide-gray-700">

                                {/* GST — DCA / sub-DCAs from the output GST config, amounts from the items */}
                                <div className="p-4 space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                            <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" /> GST Applicable?<span className="text-rose-500">*</span>
                                        </span>
                                        <YesNoToggle value={form.IsGstApplicable} onChange={handleGstApplicableChange} disabled={isBusy} />
                                    </div>

                                    {form.IsGstApplicable === 'Yes' && (
                                        <>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                <div>
                                                    <Label required>GST Type</Label>
                                                    <div className="flex gap-4 pt-2">
                                                        {['Creditable', 'Non-Creditable'].map(t => (
                                                            <label key={t} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                                                                <input type="radio" name="tradeGSTType" value={t} checked={form.GSTType === t}
                                                                    onChange={() => handleGSTTypeChange(t)} disabled={isBusy}
                                                                    className="accent-indigo-600" />
                                                                {t}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label required>Company GST</Label>
                                                    <Select value={form.CompanyGSTId} onChange={v => { setForm(p => ({ ...p, CompanyGSTId: v })); clearTaxes(); }}
                                                        disabled={!form.GSTType || companyGSTLoading || isBusy}
                                                        loading={companyGSTLoading} placeholder={!form.GSTType ? 'Select GST type first' : '— Select —'} options={companyGSTOptions} />
                                                </div>
                                                <div>
                                                    <Label required>Client GST</Label>
                                                    <Select value={form.ClientGSTId} onChange={v => { setForm(p => ({ ...p, ClientGSTId: v })); clearTaxes(); }}
                                                        disabled={!form.SubClientcode || clientGSTLoading || isBusy}
                                                        loading={clientGSTLoading} placeholder={!form.SubClientcode ? 'Select sub-client first' : '— Select —'} options={clientGSTOptions} />
                                                </div>
                                            </div>

                                            <div className="flex gap-2">
                                                {!gstAdded ? (
                                                    <button type="button" onClick={handleAddTaxes} disabled={gstConfigLoading || isBusy}
                                                        className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
                                                        {gstConfigLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Taxes
                                                    </button>
                                                ) : (
                                                    <button type="button" onClick={clearTaxes} disabled={isBusy}
                                                        className="px-3 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
                                                        <Trash2 className="h-4 w-4" /> Remove All Taxes
                                                    </button>
                                                )}
                                            </div>

                                            {gstAdded && (
                                                <>
                                                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                                                        gstAdded.sameState
                                                            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
                                                            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                                                    }`}>
                                                        <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                                        {gstAdded.sameState ? 'Same State — CGST + SGST' : 'Inter-State — IGST'}
                                                    </div>
                                                    {gstAdded.sameState ? (
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                            <ReadOnly label="Account Head" value={gstAdded.dca} />
                                                            <ReadOnly label="CGST Sub Account Head" value={gstAdded.cgstSdca} />
                                                            <ReadOnly label="CGST Amount" value={totals.cgst.toFixed(2)} />
                                                            <ReadOnly label="SGST Sub Account Head" value={gstAdded.sgstSdca} />
                                                            <ReadOnly label="SGST Amount" value={totals.sgst.toFixed(2)} />
                                                            <ReadOnly label="Total Tax" value={gstAmt.toFixed(2)} />
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                            <ReadOnly label="Account Head" value={gstAdded.dca} />
                                                            <ReadOnly label="IGST Sub Account Head" value={gstAdded.igstSdca} />
                                                            <ReadOnly label="IGST Amount" value={totals.igst.toFixed(2)} />
                                                            <ReadOnly label="Total Tax" value={gstAmt.toFixed(2)} />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* TCS */}
                                <div className="p-4 space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                            <Percent className="w-3.5 h-3.5 text-indigo-500" /> TCS Applicable?<span className="text-rose-500">*</span>
                                        </span>
                                        <YesNoToggle value={form.IsTCSApplicable} disabled={isBusy}
                                            onChange={v => setForm(p => ({ ...p, IsTCSApplicable: v, TCSAmount: v === 'Yes' ? p.TCSAmount : '' }))} />
                                    </div>
                                    {form.IsTCSApplicable === 'Yes' && (
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <ReadOnly label="TCS Account Head" value={form.TCSDCA} />
                                            <ReadOnly label="TCS Sub Account Head" value={form.TCSSDCA} />
                                            <div>
                                                <Label required>TCS Amount</Label>
                                                <div className="relative">
                                                    <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                                    <input type="text" inputMode="decimal" placeholder="0.00" value={form.TCSAmount} disabled={isBusy}
                                                        onChange={e => { const v = e.target.value; if (isValidRateInput(v)) setForm(p => ({ ...p, TCSAmount: v })); }}
                                                        className={`${inputCls} pl-8`} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Invoice Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                {[
                                    { label: 'Invoice Type', value: form.CCType },
                                    { label: 'Cost Center',  value: form.CCCode },
                                    { label: 'Client',       value: clientLabel },
                                    { label: 'Sub Client',   value: form.SubClientcode },
                                    { label: 'PO Number',    value: form.PONumber },
                                    { label: 'Invoice No.',  value: form.ClientInvoiceNo },
                                    { label: 'Invoice Date', value: formatDateForAPI(form.InvoiceDate) },
                                    { label: 'Items',        value: hasItems ? `${items.filter(r => checked[r.itemCode]).length} / ${items.length} verified` : '' },
                                ].map(({ label, value }) => (
                                    <div key={label} className="flex justify-between gap-3">
                                        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
                                        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
                                    </div>
                                ))}
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">Basic Value</dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">₹{fmt(totals.basic)}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">
                                        GST{gstAdded ? (gstAdded.sameState ? ' (CGST+SGST)' : ' (IGST)') : ''}
                                    </dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">
                                        {form.IsGstApplicable === 'Yes' ? (gstAdded ? `₹${fmt(gstAmt)}` : 'Taxes not added') : (form.IsGstApplicable || '—')}
                                    </dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">TCS</dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">
                                        {form.IsTCSApplicable === 'Yes' ? `₹${fmt(tcsAmt)}` : (form.IsTCSApplicable || '—')}
                                    </dd>
                                </div>
                            </dl>

                            <div className="px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
                                <p className="text-indigo-100 text-[10px] font-semibold uppercase tracking-wider">Total Invoice Value</p>
                                <p className="text-2xl font-black">₹ {fmt(grandTotal)}</p>
                            </div>

                            {errors.length > 0 && (
                                <div className="px-3 py-2 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-xs text-rose-700 dark:text-rose-300">
                                    <p className="flex items-center gap-1.5 font-bold mb-1"><AlertCircle className="h-3.5 w-3.5" /> Please fix the following</p>
                                    <ul className="list-disc pl-5 space-y-0.5">
                                        {errors.map(e => <li key={e}>{e}</li>)}
                                    </ul>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button type="button" onClick={handleReset} disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={isBusy || !hasItems}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                    {saveLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                                        : <><Send className="h-4 w-4" /> Submit Invoice</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientTradingInvoiceCreation;
